<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        :to="{ name: 'admin-cash-register-closings', params: { club_slug: $route.params.club_slug } }"
      >
        <v-icon>{{ mdiArrowLeft }}</v-icon>
      </v-btn>
      <h2 class="text-h6 ml-2">
        清算（レジ締）
      </h2>
    </div>

    <v-card class="mb-4">
      <v-card-text>
        <v-row align="center">
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="businessDate"
              type="date"
              label="営業日"
              outlined
              hide-details
              :disabled="!!closing"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-btn
              color="primary"
              :loading="loadingInit"
              :disabled="!businessDate || !!closing"
              @click="initDraft"
            >
              この営業日で清算を開始
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row v-if="closing">
      <!-- 左: 金種カウント -->
      <v-col
        cols="12"
        md="6"
      >
        <v-card>
          <v-card-title class="text-subtitle-1">
            金種カウント
          </v-card-title>
          <v-card-text>
            <div
              v-for="d in DENOMINATIONS"
              :key="d"
              class="d-flex align-center mb-2"
              style="gap: 12px;"
            >
              <div
                class="text-body-2"
                style="min-width: 80px;"
              >
                ¥{{ d.toLocaleString() }}
              </div>
              <v-text-field
                v-model.number="denominationCounts[d]"
                type="number"
                min="0"
                suffix="枚"
                outlined
                dense
                hide-details
                style="max-width: 120px;"
              />
              <div class="text-body-2 ml-auto">
                = {{ formatYen(d * (Number(denominationCounts[d]) || 0)) }}
              </div>
            </div>
            <v-divider class="my-3" />
            <div class="d-flex align-center justify-space-between">
              <div class="text-subtitle-1">
                金種カウント合計
              </div>
              <div class="text-h6 primary--text">
                {{ formatYen(countedTotal) }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 右: 帳簿 -->
      <v-col
        cols="12"
        md="6"
      >
        <v-card>
          <v-card-title class="text-subtitle-1">
            帳簿
          </v-card-title>
          <v-card-text>
            <v-simple-table
              v-if="expected"
              dense
            >
              <tbody>
                <tr>
                  <td>
                    開始残高（前日繰越）
                    <v-btn
                      icon
                      x-small
                      class="ml-1"
                      title="期首残高を調整"
                      @click="openAdjustOpeningDialog"
                    >
                      <v-icon x-small>
                        {{ mdiPencil }}
                      </v-icon>
                    </v-btn>
                  </td>
                  <td class="text-right">
                    {{ formatYen(expected.openingBalance) }}
                  </td>
                </tr>
                <tr
                  v-for="(amt, key) in expected.breakdown"
                  v-show="BREAKDOWN_LABELS[key] && (key !== 'other' || amt !== 0)"
                  :key="key"
                  :class="key === 'other' && amt !== 0 ? 'warning--text' : ''"
                >
                  <td>{{ BREAKDOWN_LABELS[key] }}</td>
                  <td class="text-right">
                    {{ formatYen(amt) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-divider class="my-3" />
            <div class="d-flex align-center justify-space-between">
              <div class="text-subtitle-1">
                あるべき現金額
              </div>
              <div class="text-h6 primary--text">
                {{ formatYen(closing.expectedCashAmount) }}
              </div>
            </div>
            <div class="text-caption text--secondary mt-2">
              レジへの入金は「+」、レジからの出金は「-」で表示されます。
            </div>

            <div class="mt-3">
              <v-btn
                small
                outlined
                @click="openQuickDepositDialog"
              >
                <v-icon
                  left
                  small
                >
                  {{ mdiPlus }}
                </v-icon>
                臨時入金を記録
              </v-btn>
              <span class="text-caption text--secondary ml-2">
                立替返済・釣銭補充など
              </span>
            </div>

            <v-divider class="my-3" />
            <div class="text-subtitle-2 mb-1 text--secondary">
              クレジット（参考）
            </div>
            <v-simple-table
              v-if="expected && expected.credit"
              dense
            >
              <tbody>
                <tr>
                  <td>クレジット売上</td>
                  <td class="text-right">
                    {{ formatYen(expected.credit.sales) }}
                  </td>
                </tr>
                <tr>
                  <td>クレジット売掛金回収</td>
                  <td class="text-right">
                    {{ formatYen(expected.credit.balanceReceivable) }}
                  </td>
                </tr>
                <tr class="font-weight-bold">
                  <td>クレジット合計</td>
                  <td class="text-right">
                    {{ formatYen(expected.credit.total) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <div class="text-caption text--secondary mt-2">
              クレジット決済はレジ現金に影響しないため参考表示です。
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- セクション①: 帳簿との突合（差額） -->
    <v-card
      v-if="closing"
      class="mt-4"
    >
      <v-card-title class="text-subtitle-1">
        ① 帳簿との突合
      </v-card-title>
      <v-card-text>
        <div class="d-flex align-center justify-space-between">
          <div>
            <div class="text--secondary text-caption">
              差額 = 金種カウント − あるべき現金額
            </div>
            <div class="text-caption text--secondary">
              0 が理想。差額があれば原因を下で記録してください。
            </div>
          </div>
          <div
            class="text-h5"
            :class="diffClass(diffAmount)"
          >
            {{ formatYen(diffAmount) }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- セクション②: 金種カウントの配分（繰越準備金 + 保管金） -->
    <v-card
      v-if="closing"
      class="mt-4"
    >
      <v-card-title class="text-subtitle-1">
        ② 翌日への配分
      </v-card-title>
      <v-card-text>
        <div class="text-caption text--secondary mb-3">
          金種カウントを「翌日のレジに残す金額」と「レジから抜く金額」に配分します。合計が金種カウントと一致することを確認してください。
        </div>
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model.number="nextDayReserveAmount"
              type="number"
              label="繰越準備金"
              hint="翌日のレジ開始残高として確保する金額"
              persistent-hint
              outlined
              dense
              prefix="¥"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              :value="displayedStorageAmount"
              type="number"
              label="保管金"
              :hint="storageHint"
              persistent-hint
              outlined
              dense
              prefix="¥"
              :disabled="storageBlocked"
              @input="onStorageInput"
            />
          </v-col>
        </v-row>
        <v-alert
          v-if="storageBlocked"
          type="info"
          dense
          outlined
          class="mt-2"
        >
          同日に古い給与計算で「レジ出金」（{{ formatYen(legacyRealMoneyProfitAmount) }}）が既に記録されているため、保管金欄はその金額を自動表示しています。CashRegister側での重複入力を防いでいます。
        </v-alert>
        <v-divider class="my-3" />
        <v-simple-table dense>
          <tbody>
            <tr>
              <td>金種カウント合計</td>
              <td class="text-right">
                {{ formatYen(countedTotal) }}
              </td>
            </tr>
            <tr>
              <td>繰越準備金 + 保管金</td>
              <td class="text-right">
                {{ formatYen(allocationTotal) }}
              </td>
            </tr>
            <tr class="font-weight-bold">
              <td>配分の差</td>
              <td
                class="text-right"
                :class="diffClass(allocationDelta)"
              >
                {{ formatYen(allocationDelta) }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-alert
          v-if="allocationDelta !== 0"
          type="warning"
          dense
          outlined
          class="mt-3"
        >
          配分が金種カウントと一致していません。繰越準備金と保管金を調整してください。
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- 差額の原因記録（差額あり時のみ） -->
    <v-card
      v-if="closing && diffAmount !== 0"
      class="mt-4"
    >
      <v-card-title class="text-subtitle-1">
        差額の原因記録
      </v-card-title>
      <v-card-text>
        <div
          v-for="(r, idx) in diffReasons"
          :key="idx"
          class="d-flex align-start mb-2"
          style="gap: 8px;"
        >
          <v-select
            v-model="r.category"
            :items="CATEGORY_ITEMS"
            label="カテゴリ"
            outlined
            dense
            hide-details
            style="max-width: 200px;"
          />
          <v-text-field
            v-model.number="r.amount"
            type="number"
            label="金額"
            outlined
            dense
            hide-details
            style="max-width: 160px;"
          />
          <v-text-field
            v-model="r.note"
            label="メモ"
            outlined
            dense
            hide-details
          />
          <v-btn
            icon
            @click="diffReasons.splice(idx, 1)"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </div>
        <v-btn
          text
          @click="addDiffReason"
        >
          <v-icon left>
            {{ mdiPlus }}
          </v-icon>
          原因を追加
        </v-btn>
        <v-divider class="my-3" />
        <div class="text-right">
          差額残: <span :class="diffClass(residual)">{{ formatYen(residual) }}</span>
        </div>
      </v-card-text>
    </v-card>

    <!-- 確定セクション -->
    <v-card
      v-if="closing"
      class="mt-4"
    >
      <v-card-text>
        <v-textarea
          v-if="residual !== 0"
          v-model="unresolvedReason"
          label="未解消理由（必須）"
          outlined
          rows="2"
        />
        <v-textarea
          v-model="memo"
          label="メモ（任意）"
          outlined
          rows="2"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          large
          :loading="saving"
          @click="saveDraft"
        >
          下書き保存
        </v-btn>
        <v-btn
          color="primary"
          large
          :loading="saving"
          :disabled="residual !== 0 && !unresolvedReason"
          @click="finalize"
        >
          確定する
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar
      v-model="snackbar"
      :timeout="2500"
      color="success"
    >
      {{ snackbarMessage }}
    </v-snackbar>

    <!-- 期首残高調整ダイアログ -->
    <v-dialog
      v-model="adjustOpeningDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title>期首残高を調整</v-card-title>
        <v-card-text>
          <div class="text-caption text--secondary mb-3">
            過去の不整合（記録漏れ等）で前日繰越が実態と乖離している場合に補正します。差額分の transaction が「期首残高調整」として作成されます。
          </div>
          <v-simple-table dense>
            <tbody>
              <tr>
                <td>現在の前日繰越（システム計算）</td>
                <td class="text-right">
                  {{ formatYen(expected && expected.openingBalance) }}
                </td>
              </tr>
              <tr>
                <td>実際の前日繰越（金庫の現金実残高）</td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="adjustActualBalance"
                    type="number"
                    dense
                    hide-details
                    prefix="¥"
                    style="max-width: 180px; display: inline-flex;"
                  />
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>差額（補正額）</td>
                <td
                  class="text-right"
                  :class="diffClass(adjustDelta)"
                >
                  {{ formatYen(adjustDelta) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="adjustOpeningDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :loading="adjustSaving"
            :disabled="adjustDelta === 0"
            @click="submitAdjustOpening"
          >
            補正する
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 臨時入金ダイアログ -->
    <v-dialog
      v-model="quickDepositDialog"
      max-width="480"
    >
      <v-card>
        <v-card-title>臨時入金</v-card-title>
        <v-card-text>
          <div class="text-caption text--secondary mb-3">
            立替返済・釣銭補充など、レジへの臨時入金を記録します。雑収入として帳簿に反映されます。
          </div>
          <v-text-field
            v-model.number="quickDepositAmount"
            type="number"
            min="1"
            label="金額"
            outlined
            dense
            prefix="¥"
          />
          <v-text-field
            v-model="quickDepositNote"
            label="メモ（任意）"
            placeholder="例: 立替返済、釣銭補充"
            outlined
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="quickDepositDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :loading="quickDepositSaving"
            :disabled="!quickDepositAmount || quickDepositAmount <= 0"
            @click="submitQuickDeposit"
          >
            記録する
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiArrowLeft, mdiClose, mdiPlus, mdiPencil,
} from '@mdi/js'
import CashRegisterClosingApi from '@/api/admin/CashRegisterClosing'

const DENOMINATIONS = [10000, 5000, 2000, 1000, 500, 100, 50, 10, 5, 1]

// 注: realMoneyProfit はラベル定義しない（保管金欄に統合表示するため、breakdown 表からは非表示）
const BREAKDOWN_LABELS = {
  receipt: '現金売上',
  benefit: '雑収入',
  balanceReceivable: '売掛金回収',
  earning: '現金支払い（給与等）',
  expense: '経費',
  storage: '保管金（過去のレジ締）',
  other: '未分類取引',
}

const CATEGORY_ITEMS = [
  { text: '伝票', value: 'slip' },
  { text: '領収書', value: 'receipt' },
  { text: '経費清算', value: 'expense_settlement' },
  { text: '不正持ち出し', value: 'unauthorized_takeout' },
  { text: 'その他', value: 'other' },
]

export default {
  name: 'AdminCashRegisterClosingWizard',
  setup() {
    const vm = getCurrentInstance().proxy
    const today = new Date().toISOString().slice(0, 10)
    const businessDate = ref(today)
    const closing = ref(null)
    const expected = ref(null)
    const loadingInit = ref(false)
    const saving = ref(false)
    const denominationCounts = ref(Object.fromEntries(DENOMINATIONS.map(d => [d, 0])))
    const diffReasons = ref([])
    const unresolvedReason = ref('')
    const memo = ref('')
    const nextDayReserveAmount = ref(0)
    const storageAmount = ref(0)

    const countedTotal = computed(() => DENOMINATIONS.reduce(
      (sum, d) => sum + d * (Number(denominationCounts.value[d]) || 0), 0,
    ))

    const diffAmount = computed(() => countedTotal.value - (closing.value?.expectedCashAmount || 0))

    const storageBlocked = computed(() => !!expected.value?.hasRealMoneyProfitToday)
    const legacyRealMoneyProfitAmount = computed(() => Math.abs(expected.value?.breakdown?.realMoneyProfit || 0))

    // 表示・配分計算で使う実効的な保管金額（旧システム由来があればその額を優先）
    const effectiveStorageAmount = computed(() => (storageBlocked.value
      ? legacyRealMoneyProfitAmount.value
      : Number(storageAmount.value) || 0))

    const displayedStorageAmount = computed(() => (storageBlocked.value
      ? legacyRealMoneyProfitAmount.value
      : storageAmount.value))

    const allocationTotal = computed(() => (Number(nextDayReserveAmount.value) || 0) + effectiveStorageAmount.value)

    const allocationDelta = computed(() => allocationTotal.value - countedTotal.value)

    const storageHint = computed(() => (storageBlocked.value
      ? '日報計上のレジ出金（旧システム）の値を自動表示中・編集不可'
      : '銀行預入・金庫保管など、レジから抜く金額'))

    const onStorageInput = v => {
      if (storageBlocked.value) return
      storageAmount.value = Number(v) || 0
    }

    const residual = computed(() => {
      const reasonsTotal = diffReasons.value.reduce((s, r) => s + (Number(r.amount) || 0), 0)

      return diffAmount.value - reasonsTotal
    })

    const populateFromClosing = c => {
      closing.value = c
      memo.value = c.memo || ''
      nextDayReserveAmount.value = c.nextDayReserveAmount || 0
      storageAmount.value = c.storageAmount || 0
      const nextCounts = Object.fromEntries(DENOMINATIONS.map(d => [d, 0]));
      (c.denominations || []).forEach(d => { nextCounts[d.denomination] = d.count })
      denominationCounts.value = nextCounts
      diffReasons.value = (c.diffReasons || []).map(r => ({
        category: r.category, amount: r.amount, note: r.note || '',
      }))
    }

    const fetchExpected = async () => {
      const res = await CashRegisterClosingApi.getExpectedAmount({ businessDate: businessDate.value })
      expected.value = res.data
    }

    const initDraft = async () => {
      loadingInit.value = true
      try {
        const res = await CashRegisterClosingApi.createOrFindDraft({ businessDate: businessDate.value })
        const attrs = res.data?.cashRegisterClosing?.data?.attributes
        const id = res.data?.cashRegisterClosing?.data?.id

        populateFromClosing({ id, ...attrs })

        await fetchExpected()

        // 保管金の初期値: counted - reserve（金種カウントが既に入っていて、ブロックされていなければ）
        if (storageAmount.value === 0 && countedTotal.value > 0 && !storageBlocked.value) {
          storageAmount.value = Math.max(0, countedTotal.value - nextDayReserveAmount.value)
        }
      } finally {
        loadingInit.value = false
      }
    }

    const snackbar = ref(false)
    const snackbarMessage = ref('')

    // 臨時入金ダイアログ
    const quickDepositDialog = ref(false)
    const quickDepositAmount = ref(0)
    const quickDepositNote = ref('')
    const quickDepositSaving = ref(false)

    const openQuickDepositDialog = () => {
      quickDepositAmount.value = 0
      quickDepositNote.value = ''
      quickDepositDialog.value = true
    }

    // 期首残高調整ダイアログ
    const adjustOpeningDialog = ref(false)
    const adjustActualBalance = ref(0)
    const adjustSaving = ref(false)

    const adjustDelta = computed(() => (Number(adjustActualBalance.value) || 0) - (expected.value?.openingBalance || 0))

    const openAdjustOpeningDialog = () => {
      adjustActualBalance.value = expected.value?.openingBalance || 0
      adjustOpeningDialog.value = true
    }

    const refreshAfterMutation = async () => {
      await fetchExpected()
      if (closing.value?.id) {
        const res = await CashRegisterClosingApi.getClosing(closing.value.id)
        const attrs = res.data?.cashRegisterClosing?.data?.attributes

        if (attrs) closing.value = { ...closing.value, ...attrs }
      }
    }

    const submitAdjustOpening = async () => {
      adjustSaving.value = true
      try {
        await CashRegisterClosingApi.adjustOpeningBalance({
          businessDate: businessDate.value,
          actualBalance: Number(adjustActualBalance.value),
        })
        await refreshAfterMutation()
        adjustOpeningDialog.value = false
        snackbarMessage.value = '期首残高を調整しました'
        snackbar.value = true
      } finally {
        adjustSaving.value = false
      }
    }

    const submitQuickDeposit = async () => {
      quickDepositSaving.value = true
      try {
        await CashRegisterClosingApi.quickDeposit({
          businessDate: businessDate.value,
          amount: Number(quickDepositAmount.value),
          note: quickDepositNote.value,
        })

        // 帳簿（breakdown）と closing 側の expectedCashAmount/diffAmount を同時に更新する。
        // closing のサーバー値だけマージし、ユーザーが入力中の denominationCounts などは維持。
        await fetchExpected()
        if (closing.value?.id) {
          const res = await CashRegisterClosingApi.getClosing(closing.value.id)
          const attrs = res.data?.cashRegisterClosing?.data?.attributes

          if (attrs) closing.value = { ...closing.value, ...attrs }
        }

        quickDepositDialog.value = false
        snackbarMessage.value = '臨時入金を記録しました'
        snackbar.value = true
      } finally {
        quickDepositSaving.value = false
      }
    }

    // すべての入力（金種・繰越・保管金・原因記録・メモ）を一括で下書き保存し、帳簿を再取得する。
    const persistAll = async () => {
      const denominations = DENOMINATIONS
        .filter(d => Number(denominationCounts.value[d]) > 0)
        .map(d => ({ denomination: d, count: Number(denominationCounts.value[d]) }))
      const reasons = diffReasons.value
        .filter(r => r.category)
        .map(r => ({ category: r.category, amount: Number(r.amount) || 0, note: r.note }))

      const res = await CashRegisterClosingApi.updateClosing(closing.value.id, {
        denominations,
        diffReasons: reasons,
        memo: memo.value,
        nextDayReserveAmount: Number(nextDayReserveAmount.value) || 0,
        storageAmount: Number(storageAmount.value) || 0,
      })
      const attrs = res.data?.cashRegisterClosing?.data?.attributes

      populateFromClosing({ id: closing.value.id, ...attrs })
      await fetchExpected()
    }

    const saveDraft = async () => {
      if (!closing.value) return
      saving.value = true
      try {
        await persistAll()
        snackbarMessage.value = '下書き保存しました'
        snackbar.value = true
      } finally {
        saving.value = false
      }
    }

    const finalize = async () => {
      if (!closing.value) return
      saving.value = true
      try {
        await persistAll()
        await CashRegisterClosingApi.finalizeClosing(closing.value.id, {
          unresolvedReason: residual.value === 0 ? null : unresolvedReason.value,
        })
        vm.$router.push({
          name: 'admin-cash-register-closings',
          params: { club_slug: vm.$route.params.club_slug },
        })
      } finally {
        saving.value = false
      }
    }

    const addDiffReason = () => {
      diffReasons.value.push({ category: 'slip', amount: 0, note: '' })
    }

    const formatYen = v => `¥${Number(v || 0).toLocaleString()}`

    const diffClass = v => {
      if (v === 0) return ''

      return v > 0 ? 'success--text' : 'error--text'
    }

    // 既存の draft をクエリパラメータから読み込む
    const queryId = vm.$route.query.id
    if (queryId) {
      CashRegisterClosingApi.getClosing(queryId).then(res => {
        const data = res.data?.cashRegisterClosing?.data

        if (data) {
          populateFromClosing({ id: data.id, ...data.attributes })
          businessDate.value = data.attributes.businessDate
          fetchExpected()
        }
      })
    }

    watch(businessDate, () => { expected.value = null })

    return {
      mdiArrowLeft,
      mdiClose,
      mdiPlus,
      mdiPencil,
      DENOMINATIONS,
      BREAKDOWN_LABELS,
      CATEGORY_ITEMS,
      businessDate,
      closing,
      expected,
      loadingInit,
      saving,
      denominationCounts,
      diffReasons,
      unresolvedReason,
      memo,
      nextDayReserveAmount,
      storageAmount,
      countedTotal,
      diffAmount,
      allocationTotal,
      allocationDelta,
      storageBlocked,
      legacyRealMoneyProfitAmount,
      displayedStorageAmount,
      storageHint,
      onStorageInput,
      residual,
      initDraft,
      saveDraft,
      finalize,
      addDiffReason,
      snackbar,
      snackbarMessage,
      quickDepositDialog,
      quickDepositAmount,
      quickDepositNote,
      quickDepositSaving,
      openQuickDepositDialog,
      submitQuickDeposit,
      adjustOpeningDialog,
      adjustActualBalance,
      adjustSaving,
      adjustDelta,
      openAdjustOpeningDialog,
      submitAdjustOpening,
      formatYen,
      diffClass,
    }
  },
}
</script>
